import { MaterialReactTable } from "material-react-table";
import MenuItem from "@mui/material/MenuItem";
import { Box, Button, ListItemIcon } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { getColumns } from "./columnsData/columns";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { usePVContext } from "../PVContext";
import {
  assignShellUIDs,
  resolveProjectTag,
} from "../../../../../../utils/pvUID";
import { pairFields2 } from "../pairFields";
import {
  useResponsiveShellTable,
  getResponsiveShellTableConfig,
} from "./responsiveShellTable";

const INDEPENDENT_HEAD_DIAMETER_COMPONENTS = new Set([
  "Flat Rectangular Head (L/B)",
  "Flat Rectangular Head (R/T)",
  "Flat Round Head (L/B)",
  "Flat Round Head (R/T)",
]);

const hasIndependentHeadDiameter = (component) =>
  INDEPENDENT_HEAD_DIAMETER_COMPONENTS.has(component);

// Edge Mentor code labels for the Shell Input Data table.
// These match the code_label values in edge_mentor_training_content.json.
const SHELL_EDGE_MENTOR_CODE_LABELS = {
  UID: "shellUID",
  Component: "shellComponent",
  Diameter: "vesselDiameter",
  Material: "shellRowMaterial",
  "Can Length": "shellCanLength",
  "Long Seam": "shellLongSeam",
  "Provided Thk": "shellProvidedThickness",
  Weight: "Weight",
};

const SHELL_INPUT_COLUMN_WEIGHTS = Object.freeze({
  UID: 1,
  Component: 1.8,
  Diameter: 1,
  Material: 1.3,

  "Can Length": 1.2,
  "Long Seam": 1.1,
  "Provided Thk": 1.3,

  Weight: 0.85,
});

const SHELL_ROW_ACTION_WEIGHT = 0.55;

const InputTable = (props) => {
  const { reRender, isLoading } = props;
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "DataGridView5",
  });
  const watchedRows = useWatch({
    control,
    name: "DataGridView5",
  });
  const { wbsUid } = usePVContext();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  //save cell changes in table
  const handleSaveCell = (cell, value) => {
    console.log(cell.column.id);
    // Normalise empty numeric cells to 0 so InvElectron validation never sees ""
    const numericColumns = [
      "Diameter",
      "Can Length",
      "Long Seam",
      "Provided Thk",
      "Weight",
    ];
    let finalValue = value;
    if (
      numericColumns.includes(cell.column.id) &&
      (value === "" || value === null || value === undefined)
    ) {
      finalValue = 0;
    }

    setValue(`DataGridView5.${cell.row.index}.${cell.column.id}`, finalValue);
    // Changes in Diameter applied throughout Table. And in the other tabs
    if (cell.column.id === "Diameter") {
      const currentComponent = getValues(
        `DataGridView5.${cell.row.index}.Component`,
      );

      // Flat round/rectangular heads keep their own diameter and do not force all rows.
      if (hasIndependentHeadDiameter(currentComponent)) {
        reRender();
        return;
      }

      fields.forEach((field, index) => {
        const rowComponent = getValues(`DataGridView5.${index}.Component`);
        if (!hasIndependentHeadDiameter(rowComponent)) {
          setValue(`DataGridView5.${index}.${cell.column.id}`, finalValue);
        }
      });

      pairFields2["Inside/Outside Diameter"].forEach((field, index) => {
        const isRegistered = Object.keys(errors).some(
          (error) => error === field,
        );
        if (
          !isRegistered &&
          (field.includes("txt") || field.includes("Text"))
        ) {
          register(field);
        }
        if (field.includes("txt") || field.includes("Text"))
          setValue(field, finalValue);
      });
      reRender();
    } else if (cell.column.id === "Provided Thk") {
      fields.forEach((field, index) => {
        setValue(`DataGridView5.${index}.${cell.column.id}`, finalValue);
      });
      setValue("txt_PT_CSC", finalValue);
      setValue("txt_PT_EHC", finalValue);
      setValue("txt_SPT_NC1", finalValue);
      reRender();
    } else if (cell.column.id === "Can Length") {
      // Can Length is per-row — heads and shells each keep their own value
      reRender();
    } else if (cell.column.id === "Component") {
      let material = "";
      if (value.includes("Head")) material = getValues("shellMaterial") || "";
      else material = getValues("headMaterial") || "";
      setValue(`DataGridView5.${cell.row.index}.Material`, material);
      // Re-assign all UIDs ({TAG}-SH01 / {TAG}-HE01 ...) since a component
      // change shifts the shell/head numbering.
      const tag = resolveProjectTag({
        wbsUid,
        tagNo: getValues("tagNo"),
        legacyTag: getValues("TextBox48"),
      });
      const rows = getValues("DataGridView5") || [];
      // Apply the edited component to the row being edited before counting.
      const nextRows = rows.map((row, index) =>
        index === cell.row.index ? { ...row, Component: value } : row,
      );
      assignShellUIDs(nextRows, tag).forEach((uid, index) => {
        if (uid) setValue(`DataGridView5.${index}.UID`, uid);
      });
      reRender();
    } else
      setValue(`DataGridView5.${cell.row.index}.${cell.column.id}`, finalValue);
  };

  //delete row
  const handleDeleteRow = (index) => {
    console.log({ index });
    remove(index);
    reRender();
  };

  //create new row in table
  const handleCreateNewRow = (values) => {
    append({
      UID: "",
      Component: "",
      Diameter: getValues("vesselDiameter") || 0,
      Material: "",
      "Can Length": 0,
      "Long Seam": 0,
      "Provided Thk": getValues("txt_PT_CSC") || 0,
      Weight: 0,
    });
  };

  const rawColumns = getColumns(getValues, isDark, wbsUid);

  const {
    viewportRef,
    tableWidth,
    headerFontSize,
    bodyFontSize,
    columns,
    extraWidth: rowActionsWidth,
  } = useResponsiveShellTable(
    rawColumns,
    SHELL_INPUT_COLUMN_WEIGHTS,
    SHELL_ROW_ACTION_WEIGHT,
  );

  const { mrtProps, headCellSx, bodyCellSx, editFieldSx } =
    getResponsiveShellTableConfig({
      tableWidth,
      headerFontSize,
      bodyFontSize,
    });
  return (
    <Box
      ref={viewportRef}
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <MaterialReactTable
        {...mrtProps}
        columns={columns}
        data={fields}
        editDisplayMode="table"
        enableEditing
        enableSorting={false}
        enableColumnActions={false}
        enableRowActions
        positionActionsColumn="last"
        state={{
          isLoading,
        }}
        muiTableHeadCellProps={{
          sx: headCellSx,
        }}
        muiTableBodyCellProps={({ cell }) => ({
          "data-edge-mentor":
            SHELL_EDGE_MENTOR_CODE_LABELS[cell.column.id] || undefined,

          sx: bodyCellSx,
        })}
        muiEditTextFieldProps={({ cell }) => ({
          onBlur: (event) => {
            handleSaveCell(cell, event.target.value);
          },

          sx: editFieldSx,
        })}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            header: "Actions",

            size: rowActionsWidth,
            minSize: rowActionsWidth,
            maxSize: rowActionsWidth,

            grow: false,

            enableSorting: false,
            enableColumnActions: false,

            muiTableHeadCellProps: {
              align: "center",

              sx: {
                ...headCellSx,

                width: `${rowActionsWidth}px !important`,
                minWidth: `${rowActionsWidth}px !important`,
                maxWidth: `${rowActionsWidth}px !important`,

                flex: `0 0 ${rowActionsWidth}px !important`,
                flexBasis: `${rowActionsWidth}px !important`,
                flexGrow: "0 !important",
                flexShrink: "0 !important",
              },
            },

            muiTableBodyCellProps: {
              align: "center",

              sx: {
                ...bodyCellSx,

                width: `${rowActionsWidth}px !important`,
                minWidth: `${rowActionsWidth}px !important`,
                maxWidth: `${rowActionsWidth}px !important`,

                flex: `0 0 ${rowActionsWidth}px !important`,
                flexBasis: `${rowActionsWidth}px !important`,
                flexGrow: "0 !important",
                flexShrink: "0 !important",

                p: "0 !important",
              },
            },
          },
        }}
        renderBottomToolbarCustomActions={() => (
          <Button
            color="primary"
            size="small"
            onClick={() => handleCreateNewRow()}
            variant="contained"
          >
            Add row
          </Button>
        )}
        renderRowActionMenuItems={({ row }) => [
          <MenuItem
            key={0}
            sx={{ m: 0 }}
            onClick={() => handleDeleteRow(row.index)}
          >
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            Delete
          </MenuItem>,
        ]}
      />
    </Box>
  );
};

export default InputTable;
